package org.gymly.ui.shell

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.ui.draw.alpha
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material.icons.outlined.Description
import androidx.compose.material.icons.outlined.Home
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material.icons.outlined.Schedule
import androidx.compose.material.icons.outlined.ShoppingBag
import androidx.compose.material.icons.outlined.Wallet
import androidx.compose.material3.Icon
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import org.gymly.i18n.ActiveLocale
import org.gymly.i18n.GymlyLocale
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.PortalRepo
import org.gymly.model.PortalTab
import org.gymly.model.PortalUser
import org.gymly.model.PublicCompanyBranding
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.screens.BookingsScreen
import org.gymly.ui.screens.HomeScreen
import org.gymly.ui.screens.PaymentsScreen
import org.gymly.ui.screens.PortalTermsDetail
import org.gymly.ui.screens.ProfileScreen
import org.gymly.ui.screens.SchedulesScreen
import org.gymly.ui.screens.StoreScreen

@Composable
fun PortalShell(
    user: PortalUser,
    currentTab: PortalTab,
    onTabSelected: (PortalTab) -> Unit,
    onLogout: () -> Unit,
    /** Web: sin aceptar los términos el menú se ve pero solo "Términos" responde. */
    navLocked: Boolean = false,
    /** false = el gimnasio no exige términos: la pestaña "Términos" no aparece en el menú. */
    termsVisible: Boolean = true,
) {
    val scope = rememberCoroutineScope()
    var currentUser by remember(user.id) { mutableStateOf(user) }
    var unread by remember { mutableStateOf(0L) }
    var notifTick by remember { mutableStateOf(0) }
    // Con horarios automáticos el socio no reserva: "Mis reservas" no aparece en el menú.
    var schedulesAutomatic by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        schedulesAutomatic = runCatching { PortalRepo.api.fetchHome().schedulesAutomatic }.getOrDefault(false)
    }
    val portalTabs = PortalTab.entries.filter {
        (it != PortalTab.Bookings || !schedulesAutomatic) && (it != PortalTab.Terms || termsVisible)
    }
    val mobileTabs = PortalTab.mobileBottomTabs().filter { it != PortalTab.Bookings || !schedulesAutomatic }
    val langEs = currentUser.preferredLocale?.lowercase() != "en"
    SideEffect {
        ActiveLocale.current = GymlyLocale.from(currentUser.preferredLocale)
        PortalRepo.session.preferredLocale = currentUser.preferredLocale
    }

    fun refreshUnread() {
        scope.launch {
            try {
                unread = PortalRepo.api.fetchUnreadNotificationCount()
            } catch (_: Exception) {
                // keep last known
            }
        }
    }

    LaunchedEffect(currentUser.id, currentTab, notifTick) { refreshUnread() }

    fun openNotifications() {
        onTabSelected(PortalTab.Profile)
        notifTick++
    }

    BoxWithConstraints(Modifier.fillMaxSize()) {
        val wide = maxWidth >= 900.dp
        if (wide) {
            Row(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
                PortalSidebar(
                    locked = navLocked,
                    user = currentUser,
                    tabs = portalTabs,
                    current = currentTab,
                    onSelect = onTabSelected,
                    onLogout = onLogout,
                    modifier = Modifier.width(240.dp).fillMaxHeight(),
                )
                Column(Modifier.weight(1f).fillMaxHeight()) {
                    PortalTopBar(user = currentUser)
                    Box(Modifier.weight(1f).fillMaxWidth()) {
                        PortalContent(
                            user = currentUser,
                            currentTab = currentTab,
                            onTabSelected = onTabSelected,
                            onLogout = onLogout,
                            mobile = false,
                            unreadCount = unread,
                            langEs = langEs,
                            onBell = { openNotifications() },
                            onNotifChanged = { notifTick++ },
                            onUserUpdated = { currentUser = it },
                        )
                    }
                }
            }
        } else {
            Scaffold(
                bottomBar = {
                    NavigationBar(containerColor = AdminColors.BrandPanel) {
                        mobileTabs.forEach { tab ->
                            NavigationBarItem(
                                selected = currentTab == tab,
                                enabled = !navLocked || tab == PortalTab.Terms,
                                onClick = { onTabSelected(tab) },
                                icon = { Icon(tabIcon(tab), contentDescription = tab.mobileLabel) },
                                label = { Text(tab.mobileLabel, maxLines = 1, fontSize = 12.sp) },
                                colors = NavigationBarItemDefaults.colors(
                                    selectedIconColor = AdminColors.OnBrand,
                                    selectedTextColor = AdminColors.OnBrand,
                                    unselectedIconColor = AdminColors.SidebarText,
                                    unselectedTextColor = AdminColors.SidebarText,
                                    indicatorColor = AdminColors.SidebarActiveBg,
                                ),
                            )
                        }
                    }
                },
            ) { padding ->
                Box(Modifier.padding(padding).fillMaxSize()) {
                    PortalContent(
                        user = currentUser,
                        currentTab = currentTab,
                        onTabSelected = onTabSelected,
                        onLogout = onLogout,
                        mobile = true,
                        unreadCount = unread,
                        langEs = langEs,
                        onBell = { openNotifications() },
                        onNotifChanged = { notifTick++ },
                        onUserUpdated = { currentUser = it },
                    )
                }
            }
        }
    }
}

@Composable
private fun PortalContent(
    user: PortalUser,
    currentTab: PortalTab,
    onTabSelected: (PortalTab) -> Unit,
    onLogout: () -> Unit,
    mobile: Boolean,
    unreadCount: Long,
    langEs: Boolean,
    onBell: () -> Unit,
    onNotifChanged: () -> Unit,
    onUserUpdated: (PortalUser) -> Unit,
) {
    when (currentTab) {
        PortalTab.Home -> HomeScreen(
            user = user,
            onLogout = onLogout,
            onNavigate = onTabSelected,
            mobile = mobile,
            unreadCount = unreadCount,
            langEs = langEs,
            onBell = onBell,
        )
        PortalTab.Schedules -> SchedulesScreen(
            mobile = mobile,
            unreadCount = unreadCount,
            langEs = langEs,
            onBell = onBell,
        )
        PortalTab.Bookings -> BookingsScreen(
            mobile = mobile,
            unreadCount = unreadCount,
            langEs = langEs,
            onBell = onBell,
        )
        PortalTab.Store -> StoreScreen(
            mobile = mobile,
            unreadCount = unreadCount,
            langEs = langEs,
            onBell = onBell,
        )
        PortalTab.Payments -> PaymentsScreen(
            mobile = mobile,
            unreadCount = unreadCount,
            langEs = langEs,
            onBell = onBell,
        )
        PortalTab.Terms -> PortalTermsDetail(onBack = { onTabSelected(PortalTab.Profile) }, onLogout = onLogout)
        PortalTab.Profile -> ProfileScreen(
            user = user,
            mobile = mobile,
            unreadCount = unreadCount,
            langEs = langEs,
            onBell = onBell,
            onLogout = onLogout,
            onNotifChanged = onNotifChanged,
            onUserUpdated = onUserUpdated,
        )
    }
}

@Composable
private fun PortalSidebar(
    user: PortalUser,
    tabs: List<PortalTab>,
    current: PortalTab,
    onSelect: (PortalTab) -> Unit,
    onLogout: () -> Unit,
    locked: Boolean = false,
    modifier: Modifier = Modifier,
) {
    var branding by remember { mutableStateOf<PublicCompanyBranding?>(null) }
    LaunchedEffect(Unit) {
        branding = runCatching { AdminRepo.api.publicCompany() }.getOrNull()
        org.gymly.theme.BrandTheme.apply(branding?.brandColor)
    }
    val brandName = branding?.name?.takeIf { it.isNotBlank() } ?: "Gymly"
    val logoUrl = branding?.takeIf { it.hasLogo }?.logoUrl?.takeIf { it.isNotBlank() }

    Column(
        modifier
            .background(AdminColors.Sidebar)
            .padding(vertical = 20.dp, horizontal = 12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(horizontal = 8.dp)) {
            org.gymly.ui.components.BrandLogo(logoUrl = logoUrl, name = brandName, height = 36.dp) {
                Box(
                    Modifier
                        .size(36.dp)
                        .clip(CircleShape)
                        .background(AdminColors.BrandPanel),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        brandName.take(1).lowercase(),
                        color = AdminColors.OnBrand,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }
            Text(
                brandName,
                color = AdminColors.OnBrand,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                modifier = Modifier.padding(start = 10.dp),
                maxLines = 1,
            )
        }
        Spacer(Modifier.height(28.dp))
        tabs.forEach { tab ->
            val selected = tab == current
            Row(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(if (selected) AdminColors.SidebarActiveBg else Color.Transparent)
                    .alpha(if (!locked || tab == PortalTab.Terms) 1f else 0.4f)
                    .clickable(enabled = !locked || tab == PortalTab.Terms) { onSelect(tab) }
                    .padding(horizontal = 12.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    tabIcon(tab),
                    contentDescription = null,
                    tint = if (selected) AdminColors.SidebarActive else AdminColors.SidebarText,
                    modifier = Modifier.size(20.dp),
                )
                Text(
                    tab.label,
                    color = if (selected) AdminColors.SidebarActive else AdminColors.SidebarText,
                    fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                    modifier = Modifier.padding(start = 12.dp),
                )
            }
            Spacer(Modifier.height(4.dp))
        }
        Spacer(Modifier.weight(1f))
        Text(
            user.displayName,
            color = AdminColors.SidebarText,
            fontSize = 12.sp,
            modifier = Modifier.padding(horizontal = 8.dp),
        )
        TextButton(onClick = onLogout) {
            Text("Salir", color = AdminColors.SidebarActive)
        }
    }
}

@Composable
private fun PortalTopBar(user: PortalUser) {
    Row(
        Modifier
            .fillMaxWidth()
            .background(AdminColors.BrandPanel)
            .padding(horizontal = GymlySpace.lg, vertical = GymlySpace.sm),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(AdminColors.OnBrand.copy(alpha = 0.2f)),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                user.displayName.take(1).ifBlank { "U" }.uppercase(),
                color = AdminColors.OnBrand,
                fontWeight = FontWeight.Bold,
            )
        }
    }
}

private fun tabIcon(tab: PortalTab): ImageVector = when (tab) {
    PortalTab.Home -> Icons.Outlined.Home
    PortalTab.Schedules -> Icons.Outlined.Schedule
    PortalTab.Bookings -> Icons.Outlined.CalendarMonth
    PortalTab.Store -> Icons.Outlined.ShoppingBag
    PortalTab.Payments -> Icons.Outlined.Wallet
    PortalTab.Profile -> Icons.Outlined.Person
    PortalTab.Terms -> Icons.Outlined.Description
}
